import {
  StateMachineResponse,
  LoginWithIdpConnectionRequest,
  TenantType,
} from '@wix/ambassador-iam-authentication-v1-authentication/types';
import { loginWithIdpConnection } from '@wix/ambassador-iam-authentication-v1-authentication/http';
import { WIX_TENANT_ID } from './constants';

export type IdpPopupFailureReason =
  | 'unsupported_browser'
  | 'popup_blocked'
  | 'key_setup_failed'
  | 'url_build_failed'
  | 'channel_setup_failed'
  | 'navigation_failed';

export type IdpPopupFailureDetails = Record<
  string,
  string | boolean | undefined
>;

/** Thrown when the popup could not be opened and pointed at the IdP. */
export class IdpPopupError extends Error {
  readonly reason: IdpPopupFailureReason;
  readonly details: IdpPopupFailureDetails;

  constructor(
    reason: IdpPopupFailureReason,
    details: IdpPopupFailureDetails = {},
  ) {
    super(`IdP popup failed: ${reason}`);
    this.name = 'IdpPopupError';
    this.reason = reason;
    this.details = details;
  }
}

/** Browser APIs the popup flow needs that this browser lacks. */
export const missingIdpPopupApis = (): string[] => {
  const missing: string[] = [];
  if (typeof window.BroadcastChannel !== 'function') {
    missing.push('BroadcastChannel');
  }
  if (typeof window.crypto?.randomUUID !== 'function') {
    missing.push('crypto.randomUUID');
  }
  if (!window.crypto?.subtle) {
    missing.push('crypto.subtle');
  }
  return missing;
};

// Popup/ad-blocker extensions replace window.open with their own function.
// A hint, not proof: a bound wrapper still prints [native code], and any
// harmless wrapper (e.g. a monitoring SDK) reads as non-native.
const isNativeWindowOpen = (): boolean =>
  /\[native code\]/.test(Function.prototype.toString.call(window.open));

export interface OpenIdpPopupOptions {
  idpConnectionId: string;
  ldSessionId: string;
  extraCustomPayload?: Record<string, string>;
  /**
   * Called once the popup window exists and has been pointed at the IdP login
   * URL. Not called when the browser blocked the popup or key setup failed.
   */
  onPopupOpened?: () => void;
}

/**
 * Opens a popup window to initiate IDP (Identity Provider) login flow.
 * Uses BroadcastChannel and AES-GCM encryption for secure communication
 * between the popup and the parent window.
 *
 * @param options - Configuration options for the IDP popup
 * @returns Promise resolving to the IAM platform login response
 */
export const openIdpPopup = async (
  options: OpenIdpPopupOptions,
): Promise<StateMachineResponse> => {
  const { idpConnectionId, ldSessionId, extraCustomPayload, onPopupOpened } =
    options;

  // Build request using Ambassador types for type-safety
  const request: Omit<
    LoginWithIdpConnectionRequest,
    'sessionId' | 'encryptionKey'
  > = {
    idpConnectionId,
    tenantId: WIX_TENANT_ID,
    tenantType: TenantType.ACCOUNT,
    customPayload: {
      ...extraCustomPayload,
      ldSessionId,
    },
  };

  return openPopupWithRequest(request, onPopupOpened);
};

const openPopupWithRequest = async (
  request: Omit<LoginWithIdpConnectionRequest, 'sessionId' | 'encryptionKey'>,
  onPopupOpened?: () => void,
): Promise<StateMachineResponse> => {
  // Checked before opening: without these APIs the popup could never report
  // back, and the user would be left with a blank window.
  const missingApis = missingIdpPopupApis();
  if (missingApis.length) {
    throw new IdpPopupError('unsupported_browser', {
      missingApis: missingApis.join(','),
    });
  }

  // Read before window.open, which consumes the activation.
  const hadUserActivation = navigator.userActivation?.isActive;

  // Open synchronously before any awaits — iOS Safari blocks window.open()
  // calls made after an await because the user-gesture context is lost.
  const popup = window.open('about:blank', 'oauthPopup', buildPopupFeatures());

  if (!popup) {
    throw new IdpPopupError('popup_blocked', {
      hadUserActivation,
      windowOpenIsNative: isNativeWindowOpen(),
    });
  }

  // A failure past this point would otherwise leave the popup open on about:blank.
  const failAfterOpen = (
    reason: IdpPopupFailureReason,
    error: unknown,
  ): IdpPopupError => {
    popup.close();
    return new IdpPopupError(reason, { message: errorMessage(error) });
  };
  const closePopupOnFailure = async <T>(
    reason: IdpPopupFailureReason,
    step: () => T | Promise<T>,
  ): Promise<T> => {
    try {
      return await step();
    } catch (error) {
      throw failAfterOpen(reason, error);
    }
  };

  const { sessionId, key, encryptionKey } = await closePopupOnFailure(
    'key_setup_failed',
    async () => {
      const cryptoKey = await window.crypto.subtle.generateKey(
        { name: 'AES-GCM', length: 256 },
        true,
        ['encrypt', 'decrypt'],
      );
      const rawKey = await window.crypto.subtle.exportKey('raw', cryptoKey);
      return {
        sessionId: window.crypto.randomUUID(),
        key: cryptoKey,
        encryptionKey: uint8ToBase64(new Uint8Array(rawKey)),
      };
    },
  );
  const url = await closePopupOnFailure('url_build_failed', () =>
    buildLoginUrl({ ...request, sessionId, encryptionKey }),
  );

  const channelName = `wix-idp-${sessionId}`;

  return new Promise((resolve, reject) => {
    let bc: BroadcastChannel;
    try {
      bc = new BroadcastChannel(channelName);
    } catch (error) {
      reject(failAfterOpen('channel_setup_failed', error));
      return;
    }

    bc.addEventListener('message', async (event) => {
      const { data: eventData } = event;
      const { iv, data } = eventData;

      try {
        const decryptedData = await window.crypto.subtle.decrypt(
          { name: 'AES-GCM', iv },
          key,
          data,
        );
        const loginResponse = JSON.parse(
          new TextDecoder().decode(decryptedData),
        );

        if (loginResponse.error) {
          reject({
            error: loginResponse.error,
            redirectUrl: loginResponse.redirectUrl,
            // errorData carries context such as the account email (used to
            // resolve SSO / account verification) and an optional redirectUrl.
            errorData: loginResponse.errorData,
          });
        } else {
          resolve(JSON.parse(loginResponse.response));
        }
      } catch (err) {
        reject(err);
      }
      try {
        bc.postMessage(await encryptedCloseMessage(key));
      } catch {}
      bc.close();
    });

    try {
      popup.location.href = url;
    } catch (error) {
      bc.close();
      reject(failAfterOpen('navigation_failed', error));
      return;
    }
    onPopupOpened?.();
  });
};

const errorMessage = (error: unknown): string =>
  error instanceof Error ? error.message : String(error);

const POPUP_WIDTH = 600;
const POPUP_HEIGHT = 700;

/**
 * Builds the window.open feature string for a popup centered on the screen.
 * Uses the multi-monitor-aware screen coordinates (screenLeft/screenTop and
 * outer dimensions) so the popup is centered relative to the window the user
 * is actually looking at, falling back to the primary screen.
 */
const buildPopupFeatures = (): string => {
  const dualScreenLeft = window.screenLeft ?? window.screenX ?? 0;
  const dualScreenTop = window.screenTop ?? window.screenY ?? 0;

  const width =
    window.innerWidth ?? document.documentElement.clientWidth ?? screen.width;
  const height =
    window.innerHeight ??
    document.documentElement.clientHeight ??
    screen.height;

  const left = dualScreenLeft + (width - POPUP_WIDTH) / 2;
  const top = dualScreenTop + (height - POPUP_HEIGHT) / 2;

  return `width=${POPUP_WIDTH},height=${POPUP_HEIGHT},left=${left},top=${top}`;
};

const uint8ToBase64 = (arr: Uint8Array): string =>
  btoa(
    Array(arr.length)
      .fill('')
      .map((_, i) => String.fromCharCode(arr[i]))
      .join(''),
  );

const encryptedCloseMessage = async (key: CryptoKey) => {
  const iv = window.crypto.getRandomValues(new Uint8Array(12));
  const data = await window.crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    key,
    new TextEncoder().encode('close'),
  );
  return { iv, data };
};

const buildLoginUrl = (request: LoginWithIdpConnectionRequest): string => {
  // Use Ambassador to generate the request options
  const requestOptionsFactory = loginWithIdpConnection(request);
  const requestOptions = requestOptionsFactory({
    isSSR: false,
    host: window.location.host,
  });

  // Build the full URL from the request options
  const { url, params } = requestOptions;
  if (!url) {
    throw new Error('Failed to build IDP login URL');
  }
  const queryString = params?.toString() || '';
  return queryString ? `${url}?${queryString}` : url;
};
